import { Component, inject, NgZone, signal } from '@angular/core'
import { Router, RouterModule } from '@angular/router'

import { IonicModule, Platform } from '@ionic/angular'
import { QuangAuthService } from 'quang/auth'
import { QuangLoaderComponent } from 'quang/loader'
import { TranslocoService } from '@jsverse/transloco'
import { AsideComponent } from './core/aside/aside.component'

import { environment } from '../environments/environment'
import { FeedbackHistoryControllerService } from './feedbacks/feedback-history/store/feedback-history-controller.service'
import { CounterController } from './core/counter-store/counter-controller.service'
import { Preferences } from '@capacitor/preferences'

import packageInfo from '../../package.json'
import { OAuthService } from 'angular-oauth2-oidc'

import { ActionPerformed, PushNotifications, Token, } from '@capacitor/push-notifications'
import { StatusBar, Style } from '@capacitor/status-bar'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { SplashScreen } from '@capacitor/splash-screen'

import { UserService } from './user.service'
import { NotificationService } from './apiModule'

@Component({
    selector: 'pf-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    
    imports: [
      RouterModule,
      IonicModule,
      QuangLoaderComponent,
      AsideComponent
    ]
})
export class AppComponent {
  appVersion: string = packageInfo.version
  readonly inForeground = signal(true)
  readonly inLogin = signal(true)

  private readonly platform = inject(Platform)
  private readonly translocoService = inject(TranslocoService)
  private readonly router = inject(Router)
  private readonly notificationService = inject(NotificationService)
  private readonly zone = inject(NgZone)
  private readonly oauthService = inject(OAuthService)
  private readonly authService = inject(QuangAuthService)
  private readonly historyController = inject(FeedbackHistoryControllerService)
  private readonly counterController = inject(CounterController)
  private readonly userService = inject(UserService)
  readonly user = this.userService.user
  readonly isLogged = this.authService.isAuthenticated

  constructor () {
    environment.isApp = this.platform.is("capacitor");
    this.initializeApp()
  }

  triggerLogout (): void {
    this.authService.logout().finally(() => {
      this.triggerLogin()
    })
  }

  triggerLogin(): void {
    this.oauthService.logOut(true);
    this.oauthService.stopAutomaticRefresh();
    window.location.reload();
  }

  initializeApp(): void {
    this.platform.ready().then(() => {
      if (environment.isApp) {
        Device.getId().then((idData) => {
          Device.getInfo().then((device) => {
            App.getInfo().then((i) => {
              environment.deviceInfo = {
                appVersion: i.version,
                appBuild: i.build,
                appId: i.id,
                appName: i.name,
                operatingSystem: device.operatingSystem,
                osVersion: device.osVersion,
                platform: device.platform,
                model: device.model,
                manufacturer: device.manufacturer,
                uuid: idData.identifier,
                isVirtual: device.isVirtual,
              };

              SplashScreen.hide()
              StatusBar.setStyle({
                style: Style.Light,
              })
              this.platform.pause.subscribe(async () => {
                this.inForeground.set(false)
                console.log('Pause event detected')
              })
              this.platform.resume.subscribe(async () => {
                this.inForeground.set(true)
                console.log('Resume event detected')
                if (!this.oauthService.hasValidAccessToken() && !this.inLogin()) {
                  this.oauthService
                    .refreshToken()
                    .then((data) => {
                      console.log('token refreshed succesfully')
                    })
                    .catch((error) => {
                      console.log('token refresh did not work: ' + error)
                      // eseguo il logout poichè anche il refresh token risulta scaduto
                      this.triggerLogout()
                    });
                }
                if (
                  this.user() &&
                  localStorage.getItem('push-notification-token')
                ) {
                  this.readNotifications(false, null);
                }
              });

              this.setOpenUri();
              this.translocoService.setActiveLang('en')
              this.pushNotificationsOnInit();
              this.setAppPreferences();
            });
          });
        });
      } else {
        this.setOpenUri();
        this.translocoService.setActiveLang('en')
        this.pushNotificationsOnInit();
        this.setAppPreferences();
      }
      this.startupAuth()
    });
  }

  startupAuth (): void {
    this.inLogin.set(true)
    this.authService.init().then(() => {
      this.inLogin.set(false)
    })
  }

  startupLoginBrowser (uri): void {
    window.location.href = uri
  }

  setOpenUri (): void {
    ;(window as any).createLoginPage = (uri: string) => {
      if (environment.isApp) {
        this.startupLoginBrowser(uri)
      } else {
        window.location.href = uri;
      }
    };
  }

  setLanguage(): void {
    // da chiamare se non si inizializza la lingua con il toen jwt
    this.translocoService.setActiveLang(window.navigator.language);
  }

  setAppPreferences(): void {
    if (environment.isApp) {
      Preferences.set({ key: 'appVersion', value: packageInfo.version })
    }
  }

  readNotifications(navigate: boolean, notification: ActionPerformed): void {
    const user = this.user()
    if (!user) {
      return
    }

    this.notificationService
      .readAll2({
        appName: "productFeedback",
        appVersion: environment.deviceInfo.appVersion,
        channelId: environment.deviceInfo.uuid,
        channelType: environment.deviceInfo.platform,
        // channelToken: null,
        userId: user.username,
      })
      .subscribe(() => {
        this.counterController.retrieveUnreadCount();
        PushNotifications.removeAllDeliveredNotifications();
        if (navigate) {
          if (notification.notification.title?.includes("chat")) {
            this.historyController.updateSearch({
              status: "OPEN",
              onlyUnreadFeedback: false,
              onlyUnreadMessages: true,
            })
          }
          this.zone.run(
            async () => await this.router.navigate(["/feedbacks/history"])
          );
        }
      });
  }

  pushNotificationsOnInit(): void {
    console.log("pushNotificationsOnInit", environment.isApp);
    if (environment.isApp) {
      PushNotifications.requestPermissions().then((result) => {
        if (result.receive === "granted") {
          // Register with Apple / Google to receive push via APNS/FCM
          PushNotifications.register().then(() => {
            console.log("registered push");

            PushNotifications.addListener("registration", (token: Token) => {
              console.log("registration", token);
              localStorage.setItem('push-notification-token', token.value)
            });

            PushNotifications.addListener("registrationError", (error: any) => {
              console.log("Error on registration: " + JSON.stringify(error));
            });

            PushNotifications.addListener(
              "pushNotificationActionPerformed",
              (notification: ActionPerformed) => {
                this.readNotifications(true, notification);
              }
            );

            PushNotifications.removeAllDeliveredNotifications();
          });
        }
      });
    }
  }

}
